/**
 * Where to move the window so that data found at `boundaryMs` is on screen.
 *
 * Keeps the user's current span and centers it on the boundary, so the found
 * data lands mid-plot with context on both sides. Clamped so the window never
 * ends in the future and never inverts.
 */
export const boundaryJumpWindow = (
  boundaryMs: number,
  spanMs: number,
  nowMs: number
): { from: string; to: string } => {
  const half = Math.max(1, spanMs) / 2
  let from = boundaryMs - half
  let to = boundaryMs + half

  // A window centered on recent data can overshoot `now`; slide it back so it
  // ends at `now` rather than silently showing an empty future tail.
  if (to > nowMs) {
    const shift = to - nowMs
    from -= shift
    to = nowMs
  }

  if (from >= to) {
    from = to - Math.max(1, spanMs)
  }

  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() }
}

/** A lookback window ending just before `fromMs`, for probing earlier data. */
export const beforeProbeWindow = (
  fromMs: number,
  lookbackMs: number
): { start: string; end: string } => ({
  start: new Date(fromMs - Math.max(1, lookbackMs)).toISOString(),
  end: new Date(fromMs).toISOString(),
})

/** The window from `toMs` up to `now`, for probing later data. */
export const afterProbeWindow = (
  toMs: number,
  nowMs: number
): { start: string; end: string } | null => {
  if (nowMs <= toMs) return null
  return {
    start: new Date(toMs).toISOString(),
    end: new Date(nowMs).toISOString(),
  }
}
